import { useEffect, useMemo, useState } from "react";
import {
  TrendingUp,
  Activity,
  Target,
  Trophy,
  ArrowUpRight,
  Info,
  CheckCircle2,
  Hourglass,
  Flame,
  Dices,
} from "lucide-react";
import { api } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n, sym = "৳") =>
  `${sym}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function TurnoverPage() {
  const [data, setData] = useState(null);
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, b] = await Promise.all([
          api.get("/profile/turnover"),
          api.get("/profile/bets").catch(() => ({ data: { items: [] } })),
        ]);
        setData(t.data);
        setBets(b.data?.items || []);
      } catch {
        setData({
          total_turnover: 0,
          bet_count: 0,
          currency: "BDT",
          turnover_enabled: true,
          turnover_required: 0,
          turnover_completed: 0,
          turnover_remaining: 0,
          turnover_progress: 0,
          can_withdraw: true,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const symbol = data?.currency === "BDT" ? "৳" : "$";

  /* ---------- derive period stats from bets ---------- */
  const { today, week, month, last7 } = useMemo(() => {
    const now = Date.now();
    const DAY = 86400000;
    let today = 0;
    let week = 0;
    let month = 0;
    const buckets = Array.from({ length: 7 }, () => 0);
    (bets || []).forEach((b) => {
      const ts = b.created_at ? new Date(b.created_at).getTime() : 0;
      const amt = Number(b.amount || 0);
      if (!ts) return;
      const age = now - ts;
      if (age < DAY) today += amt;
      if (age < 7 * DAY) week += amt;
      if (age < 30 * DAY) month += amt;
      const bucketIdx = Math.floor(age / DAY);
      if (bucketIdx >= 0 && bucketIdx < 7) buckets[6 - bucketIdx] += amt;
    });
    return { today, week, month, last7: buckets };
  }, [bets]);

  const avgStake =
    data?.bet_count > 0 ? Math.round((data.total_turnover || 0) / data.bet_count) : 0;

  const progressPct = Math.round((data?.turnover_progress || 0) * 100);
  const wagerActive = data?.turnover_enabled && data?.turnover_required > 0;

  return (
    <ProfileShell title="Turnover">
      <div className="py-2 pb-10 space-y-4" data-testid="turnover-page">
        {/* -------- Hero lifetime card -------- */}
        <div
          className="rounded-3xl relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #0d1c15 0%, #0a1119 55%, #0d1c15 100%)",
            border: "1px solid rgba(25,165,92,0.22)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
          }}
        >
          {/* radial accents */}
          <div
            aria-hidden
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(25,165,92,0.35) 0%, transparent 60%)",
            }}
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full blur-3xl pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(245,196,81,0.18) 0%, transparent 60%)",
            }}
          />
          {/* grid */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />

          <div className="relative z-10 p-6">
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 px-2.5 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-[10px] font-extrabold uppercase tracking-[0.18em] text-emerald-300">
                <Activity size={11} /> Lifetime Turnover
              </div>
              <div
                className="inline-flex items-center gap-1 px-2 h-6 rounded-full border text-[10px] font-bold uppercase tracking-wider"
                style={{
                  background: data?.can_withdraw
                    ? "rgba(25,165,92,0.15)"
                    : "rgba(245,158,11,0.12)",
                  borderColor: data?.can_withdraw
                    ? "rgba(25,165,92,0.35)"
                    : "rgba(245,158,11,0.35)",
                  color: data?.can_withdraw ? "#34d399" : "#fbbf24",
                }}
              >
                {data?.can_withdraw ? (
                  <>
                    <CheckCircle2 size={10} /> Withdrawable
                  </>
                ) : (
                  <>
                    <Hourglass size={10} /> Wagering needed
                  </>
                )}
              </div>
            </div>

            <div
              data-testid="turnover-total"
              className="mt-5 flex items-baseline gap-1 leading-none"
            >
              <span className="text-emerald-400/90 text-2xl font-bold">{symbol}</span>
              <span
                className="text-[46px] sm:text-[56px] font-black tabular-nums tracking-tight"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #ffffff 0%, #bfe8d0 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {loading ? "···" : fmtInt(data?.total_turnover)}
              </span>
            </div>

            <div className="mt-3 flex items-center gap-4 flex-wrap text-xs">
              <span className="text-white/55 inline-flex items-center gap-1.5">
                <Dices size={12} className="text-emerald-400" />
                {fmtInt(data?.bet_count)} bets placed
              </span>
              <span className="text-white/30">·</span>
              <span className="text-white/55 inline-flex items-center gap-1.5">
                <Target size={12} className="text-amber-400" />
                Avg stake {fmtMoney(avgStake, symbol)}
              </span>
            </div>

            {/* Sparkline last 7 days */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold mb-2 flex items-center gap-1.5">
                <TrendingUp size={10} /> Last 7 days
              </div>
              <Sparkline values={last7} />
            </div>
          </div>
        </div>

        {/* -------- Wagering progress (only if active) -------- */}
        {wagerActive && (
          <div
            className="rounded-2xl p-5 border"
            style={{
              background:
                "linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.02) 100%)",
              borderColor: "rgba(245,158,11,0.25)",
            }}
          >
            <div className="flex items-center justify-between mb-2.5">
              <div className="inline-flex items-center gap-2 text-amber-300 text-[11px] font-bold uppercase tracking-[0.18em]">
                <Flame size={13} /> Wagering Requirement
              </div>
              <span className="text-amber-300 font-black text-xl tabular-nums">
                {progressPct}%
              </span>
            </div>

            <div className="relative h-3 rounded-full overflow-hidden bg-white/5 border border-white/5">
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700"
                style={{
                  width: `${Math.max(1, progressPct)}%`,
                  background:
                    "linear-gradient(90deg, #fbbf24 0%, #f59e0b 60%, #f97316 100%)",
                  boxShadow: "0 0 14px rgba(251,191,36,0.55)",
                }}
              />
              {/* shine */}
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)",
                  animation: "shineTO 2.2s ease-in-out infinite",
                }}
              />
            </div>

            <div className="flex items-center justify-between text-xs mt-2.5">
              <span className="text-white/55">
                Done{" "}
                <span className="text-white font-bold">
                  {fmtMoney(data?.turnover_completed, symbol)}
                </span>
              </span>
              <span className="text-white/55">
                Remaining{" "}
                <span className="text-amber-300 font-bold">
                  {fmtMoney(data?.turnover_remaining, symbol)}
                </span>
              </span>
              <span className="text-white/55">
                Target{" "}
                <span className="text-white font-bold">
                  {fmtMoney(data?.turnover_required, symbol)}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* -------- Period stat grid -------- */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            icon={<Flame size={15} />}
            tone="red"
            label="Today"
            value={fmtMoney(today, symbol)}
            hint="Last 24 hours"
          />
          <StatCard
            icon={<TrendingUp size={15} />}
            tone="emerald"
            label="This week"
            value={fmtMoney(week, symbol)}
            hint="Last 7 days"
          />
          <StatCard
            icon={<Trophy size={15} />}
            tone="amber"
            label="This month"
            value={fmtMoney(month, symbol)}
            hint="Last 30 days"
          />
          <StatCard
            icon={<Target size={15} />}
            tone="blue"
            label="Avg stake"
            value={fmtMoney(avgStake, symbol)}
            hint="Per bet"
          />
        </div>

        {/* -------- Recent bets peek (if any) -------- */}
        {bets && bets.length > 0 && (
          <div
            className="rounded-2xl p-4 border border-white/5"
            style={{ background: "rgba(255,255,255,0.02)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-bold">
                Recent bets
              </div>
              <a
                href="/profile/bets"
                className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300 font-semibold"
              >
                View all <ArrowUpRight size={11} />
              </a>
            </div>
            <div className="space-y-2">
              {bets.slice(0, 3).map((b, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 py-1.5"
                  data-testid={`turnover-recent-${i}`}
                >
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center"
                    style={{
                      background: "linear-gradient(135deg,#19a55c,#0f5e34)",
                    }}
                  >
                    <Dices size={14} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-semibold truncate">
                      {b.game || b.provider || "Bet"}
                    </div>
                    <div className="text-white/45 text-[11px]">
                      {b.created_at
                        ? new Date(b.created_at).toLocaleString()
                        : ""}
                    </div>
                  </div>
                  <div className="text-white font-bold tabular-nums">
                    {fmtMoney(b.amount, symbol)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* -------- Footer note -------- */}
        <div className="flex gap-2 p-3 rounded-xl border border-white/5 bg-white/[0.02] text-[11px] text-white/45 leading-relaxed">
          <Info size={14} className="text-white/40 shrink-0 mt-0.5" />
          <span>
            Turnover updates automatically after each settled bet. Bonus
            wagering requirements (if any) follow the individual promotion
            Terms &amp; Conditions.
          </span>
        </div>
      </div>

      <style>{`
        @keyframes shineTO {
          0%,100% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
        }
      `}</style>
    </ProfileShell>
  );
}

/* ---------- tiny sparkline without any chart lib ---------- */
function Sparkline({ values = [] }) {
  const max = Math.max(1, ...values);
  const W = 280;
  const H = 52;
  const step = W / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => {
    const x = i * step;
    const y = H - (v / max) * (H - 4) - 2;
    return [x, y];
  });
  const d =
    pts.length > 1
      ? "M " + pts.map((p) => p.join(" ")).join(" L ")
      : `M 0 ${H / 2} L ${W} ${H / 2}`;
  const area = d + ` L ${W} ${H} L 0 ${H} Z`;

  return (
    <div className="relative h-14">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="absolute inset-0"
      >
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#21c46e" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#21c46e" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#spark-fill)" />
        <path
          d={d}
          fill="none"
          stroke="#21c46e"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {pts.map((p, i) => (
          <circle
            key={i}
            cx={p[0]}
            cy={p[1]}
            r={i === pts.length - 1 ? 3 : 1.5}
            fill={i === pts.length - 1 ? "#ffffff" : "#21c46e"}
            stroke={i === pts.length - 1 ? "#21c46e" : "none"}
            strokeWidth="1.5"
          />
        ))}
      </svg>
      <div className="absolute inset-x-0 bottom-0 flex justify-between text-[9px] text-white/30 px-0.5">
        {["7d", "6d", "5d", "4d", "3d", "2d", "Today"].map((l) => (
          <span key={l}>{l}</span>
        ))}
      </div>
    </div>
  );
}

/* ---------- stat card ---------- */
const toneMap = {
  red: { bg: "rgba(244,63,94,0.1)", border: "rgba(244,63,94,0.25)", text: "#fb7185" },
  emerald: { bg: "rgba(25,165,92,0.1)", border: "rgba(25,165,92,0.28)", text: "#34d399" },
  amber: { bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.28)", text: "#fbbf24" },
  blue: { bg: "rgba(59,130,246,0.1)", border: "rgba(59,130,246,0.28)", text: "#60a5fa" },
};

function StatCard({ icon, tone = "emerald", label, value, hint }) {
  const t = toneMap[tone] || toneMap.emerald;
  return (
    <div
      className="rounded-2xl p-4 relative overflow-hidden transition-transform hover:-translate-y-0.5"
      style={{
        background: "linear-gradient(135deg, #111a24 0%, #0b131c 100%)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <div className="flex items-center justify-between">
        <span
          className="inline-flex items-center gap-1 px-2 h-6 rounded-md text-[10px] font-bold uppercase tracking-wider"
          style={{ background: t.bg, color: t.text, borderColor: t.border }}
        >
          {icon} {label}
        </span>
      </div>
      <div className="text-white text-[22px] sm:text-2xl font-extrabold tabular-nums mt-3 leading-none">
        {value}
      </div>
      {hint && (
        <div className="text-white/40 text-[10px] mt-1.5 uppercase tracking-wider">
          {hint}
        </div>
      )}
      {/* subtle corner glow */}
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-24 h-24 rounded-full blur-2xl pointer-events-none"
        style={{ background: t.bg }}
      />
    </div>
  );
}
